// Test function to verify object isolation functionality
import { getXeokit } from './XeokitSingleton';

export function testObjectIsolation(objectId: string): { success: boolean; message: string; visibilityState?: any } {
    const viewer = getXeokit();
    if (!viewer) {
        return { success: false, message: 'Xeokit viewer not available' };
    }

    try {
        // Extract numeric ID from formats like "CL_W1[493697]" or just "493697"
       // const numericId = objectId.match(/\[(\d+)\]$/) ? objectId.match(/\[(\d+)\]$/)![1] : objectId;
        
        // Check if object exists
        if (!viewer.scene.objects[objectId]) {
            return { success: false, message: `Object ${objectId} not found in scene` };
        }

        // Get visibility state before isolation
        const allObjectIds = viewer.scene.objectIds;
        const visibleObjectsBefore = allObjectIds.filter(id => viewer.scene.objects[id].visible);
        
        // Perform isolation
        viewer.scene.setObjectsVisible(allObjectIds, false);
        viewer.scene.setObjectsVisible([objectId], true);
        
        // Get visibility state after isolation
        const visibleObjectsAfter = allObjectIds.filter(id => viewer.scene.objects[id].visible);
        
        const visibilityState = {
            totalObjects: allObjectIds.length,
            visibleBefore: visibleObjectsBefore.length,
            visibleAfter: visibleObjectsAfter.length,
            targetObjectVisible: viewer.scene.objects[objectId].visible,
            isolatedObjectId: objectId
        };

        const success = visibleObjectsAfter.length === 1 && visibleObjectsAfter[0] === objectId;
        
        return {
            success,
            message: success 
                ? `Successfully isolated object ${objectId} (${objectId}). Only 1 object visible out of ${allObjectIds.length} total.`
                : `Isolation failed. ${visibleObjectsAfter.length} objects visible instead of 1.`,
            visibilityState
        };
        
    } catch (error) {
        return { 
            success: false, 
            message: `Error during isolation test: ${error instanceof Error ? error.message : 'Unknown error'}` 
        };
    }
}

// Function to restore all objects visibility
export function restoreAllObjects(): { success: boolean; message: string } {
    const viewer = getXeokit();
    if (!viewer) {
        return { success: false, message: 'Xeokit viewer not available' };
    }

    try {
        const allObjectIds = viewer.scene.objectIds;
        viewer.scene.setObjectsVisible(allObjectIds, true);
        
        const visibleObjects = allObjectIds.filter(id => viewer.scene.objects[id].visible);
        
        return {
            success: visibleObjects.length === allObjectIds.length,
            message: `Restored visibility for ${visibleObjects.length}/${allObjectIds.length} objects`
        };
    } catch (error) {
        return { 
            success: false, 
            message: `Error restoring objects: ${error instanceof Error ? error.message : 'Unknown error'}` 
        };
    }
}

// Make functions available globally for testing in browser console
declare global {
    interface Window {
        testObjectIsolation: typeof testObjectIsolation;
        restoreAllObjects: typeof restoreAllObjects;
    }
}

if (typeof window !== 'undefined') {
    window.testObjectIsolation = testObjectIsolation;
    window.restoreAllObjects = restoreAllObjects;
}
